import React, { useCallback, useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Stack,
  Box,
  Paper,
  Typography,
  useMediaQuery,
  MenuItem,
  MenuList,
  InputAdornment,
  IconButton,
} from '@mui/material';
import useBludataLogoThemed from '../utility_objects/const/imageUrls';
import json2mq from 'json2mq';
import EmptyPage from '../utility_objects/EmptyPage';
import useLogoutMSAL from './useLogoutMSAL';
import { getWorkplaces } from './api/GetWorkplaces';
import { BluLoader, BluTitle } from '@bludata/components';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface Props {
  onPdvSet: (workplaceRef: string) => void;
}

function LoginChoosePdvPage(props: Props) {
  const { logoFocusUrl } = useBludataLogoThemed();
  const { onPdvSet } = props;
  const [workplaces, setWorkplaces] = useState([]);
  const [workplaceRef, setWorkplaceRef] = useState('');
  const { logout } = useLogoutMSAL();
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const matchesSmall = useMediaQuery(
    json2mq({
      maxWidth: 1024,
    }),
  );

  const matchesMedium = useMediaQuery(
    json2mq({
      maxWidth: 1500,
    }),
  );

  const handleSubmit = useCallback(() => {
    onPdvSet(workplaceRef);
  }, [onPdvSet, workplaceRef]);

  useEffect(() => {
    const getAsyncWorkplaces = async () => {
      setIsLoading(true);
      const result = await getWorkplaces();
      console.log('Fetched workplaces:', result);
      setWorkplaces(result);
      setIsLoading(false);
    };
    getAsyncWorkplaces();
  }, []);

  const getValues = useCallback(() => {
    return (workplaces ?? []).filter((item) =>
      item.name.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, workplaces]);

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && workplaceRef !== '') {
      handleSubmit();
    }
  };

  const onAnnullaClick = () => {
    logout(true, 'explicit');
  };

  return (
    <EmptyPage>
      <BluLoader open={isLoading} />
      <Paper
        elevation={0}
        sx={{
          width: 400,
          p: '40px 30px',
          zIndex: 9999,
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          position: matchesSmall ? undefined : 'absolute',
          right: matchesMedium ? '10%' : '20%',
        }}
      >
        <Stack flexDirection="column">
          <Stack flexDirection="row" justifyContent="center">
            <Box
              sx={{
                margin: '10px 0px',
                marginBottom: '40px',
                height: 32,
                width: 190,
                backgroundImage: `url(${logoFocusUrl})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'auto 100%',
                backgroundPosition: 'center',
              }}
            />
          </Stack>
          <Stack flexDirection="column" spacing={2}>
            <Typography fontSize="16px" textAlign="center">
              Hai effettuato l’accesso con un account che gestisce più punti
              vendita.
              <br />
              Seleziona il punto vendita con cui vuoi accedere.
              <br />
              Potrai sempre cambiare punto vendita cliccando sul nome in alto a
              sinistra vicino al logo Focus Cloud.
            </Typography>

            <Stack flexDirection="column" gap={1}>
              <TextField
                autoFocus
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment:
                      searchValue !== '' ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchValue('');
                            }}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  },
                }}
                label="Ricerca il punto vendita"
                // sx={{ maxWidth: 250 }}
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                }}
              />
              <Stack
                sx={{
                  height: 250,
                  border: '1px solid var(--divider-color)',
                  borderRadius: 1,
                }}
              >
                <MenuList sx={{ overflow: 'auto' }}>
                  {getValues().map((item) => (
                    <MenuItem
                      onClick={() => setWorkplaceRef(item.workplaceRef)}
                      selected={item.workplaceRef === workplaceRef}
                      key={item.workplaceRef}
                    >
                      <Stack
                        flexDirection="column"
                        sx={{
                          ml: 1,
                          cursor: 'pointer',
                        }}
                      >
                        <BluTitle text={item.name} />
                        <Typography fontWeight="bold" lineHeight={1.2}>
                          {item.shortCode}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            flexDirection="row"
            justifyContent="end"
            gap={1}
            sx={{ pt: 2 }}
          >
            <Button
              sx={{
                fontWeight: 400,
                height: 40,
                width: 120,
                mr: '10px',
                justifyContent: 'center',
              }}
              variant="outlined"
              color="primary"
              onClick={onAnnullaClick}
            >
              Annulla
            </Button>
            <Button
              sx={{
                fontWeight: 400,
                height: 40,
                width: 120,
                mr: '10px',
                justifyContent: 'center',
              }}
              disabled={workplaceRef === ''}
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Conferma
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </EmptyPage>
  );
}

export default LoginChoosePdvPage;
